import { useMemo } from 'react';
import { appConfig } from '../config/appConfig';
import {api} from './api';
//userapiservice
// Create an axios instance
const apiClient = api;



export function useApiService() {
  const auth = useMemo(() => ({
    login: async (data) => {
      try {
        
        const response = await apiClient.post('/v1/auth/login', data, {
          skipAuthRedirect: true,
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        // Log login activity
        if (response.data?.user) {
          try {
            await logUserActivity({
              userId: response.data.user.id,
              actorId: response.data.user.id,
              customerId: response.data.user.customerId,
              action: 'User logged in',
              category: 'AUTH',
              details: {
                timestamp: new Date().toISOString(),
                email: response.data.user.email
              }
            });
          } catch (error) {
            console.error('Error logging login activity:', error);
          }
        }
        
        return response;
      } catch (error) {
        console.error('Error during login:', error);
        throw error;
      }
    },
    logout: async () => {
      try {
        const userId = localStorage.getItem('userId');
        const customerId = localStorage.getItem('customerId');
        const userEmail = localStorage.getItem('userEmail');
        
        // Log logout activity before actual logout
        if (userId && customerId) {
          try {
            await logUserActivity({
              userId,
              actorId: userId,
              customerId,
              action: 'User logged out',
              category: 'AUTH',
              details: {
                timestamp: new Date().toISOString(),
                email: userEmail
              }
            });
          } catch (error) {
            console.error('Error logging logout activity:', error);
          }
        }
        
        return apiClient.post('/v1/auth/logout');
      } catch (error) {
        console.error('Error during logout:', error);
        throw error;
      }
    },
    getCurrentUser: () => apiClient.get('/v1/auth/me'),
    forgotPassword: (email, productId) => apiClient.post('/v1/auth/forgot-password', {
      email,
      productId: productId ?? appConfig.productId,
    }, { skipAuthRedirect: true }),
    resetPassword: (token, newPassword) => apiClient.post('/v1/auth/reset-password', { token, newPassword }, { skipAuthRedirect: true }),
    resetLoginAttempts: async (email, ip) => {
      try {
        const response = await apiClient.post('/v1/auth/reset-login-attempts', {
          email,
          ip
        });
        return response.data;
      } catch (error) {
        console.error('Error resetting login attempts:', error);
        throw error;
      }
    }
  }), []);

  // Helper function to log user activities
  const logUserActivity = async (activityData) => {
    try {
      const response = await apiClient.post('/v1/user-activity', activityData);
      return response.data;
    } catch (error) {
      console.error('Error logging activity:', error);
      throw error;
    }
  };

  const users = useMemo(() => ({
    getAll: (customerId, productId, tenantID, options = {}) =>
      apiClient.post('/v1/application-users', {
        customerId,
        productId,
        tenantID,
        page: options.page || 1,
        limit: options.limit || 10,
        sortField: options.sortField || 'fullName',
        sortOrder: options.sortOrder || 'asc',
        search: options.search || ''
      }),
    invite: (userData) => {
      const tenantId = localStorage.getItem('tenantId');
      return apiClient.post('/v1/application-users/invite', { ...userData, tenantId });
    },
    updateRole: async (userId, data) => {
      try {
        const response = await apiClient.put(`/v1/application-users/${userId}/role`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating role:', error);
        throw error;
      }
    },
    updateStatus: async (userId, data) => {
      const response = await apiClient.put(`/v1/application-users/${userId}/status`, data);
      return response.data;
    },
    delete: (userId) => {
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }
      return apiClient.delete(`/v1/application-users/${userId}?tenantId=${tenantId}`);
    },
    resetUserPassword: (userId, newPassword) => {
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }
      return apiClient.post(
        `/v1/application-users/${userId}/reset-password?tenantId=${tenantId}`,
        { newPassword }
      );
    },
    removeFromApplication: (userId) => {
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }
      return apiClient.delete(`/v1/application-users/${userId}?tenantId=${tenantId}`);
    },
    updateUserProfile: async (userId, profileData) => {
      try {
        if (!userId) {
          throw new Error('User ID is required');
        }
        
        
        const response = await apiClient.put(`/v1/application-users/${userId}/profile`, profileData);
        
        if (response.data?.user) {
          return response.data.user;
        }
        throw new Error('Invalid response format');
      } catch (error) {
        console.error('API Error:', error.response?.data || error.message);
        throw error;
      }
    },
    uploadProfileImage: async (formData, userId) => {
      try {
        if (!userId) throw new Error('User ID is required');
        
        const response = await apiClient.post(`/v1/application-users/${userId}/profile-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        console.error('Profile image upload error:', error);
        throw error;
      }
    },
    getProfileImageUrl: async (userId) => {
      try {
        if (!userId) throw new Error('User ID is required');
        const response = await apiClient.get(`/v1/application-users/profile-image/${userId}`);
        return response.data;
      } catch (error) {
        console.error('Error getting profile image URL:', error);
        throw error;
      }
    },
    getCurrentUser: async (userId) => {
      try {
        if (!userId) {
          throw new Error('User ID is required');
        }
        const response = await apiClient.get(`/v1/application-users/${userId}`);
        return {
          ...response.data,
          profileImage: response.data.profileImage || null
        };
      } catch (error) {
        console.error('Error fetching user details:', error);
        throw error;
      }
    },
    getPendingInvitations: async (options = {}) => {
      try {
        const tenantId = localStorage.getItem('tenantId');
        if (!tenantId) {
          throw new Error('Tenant ID is required');
        }
        
        const params = new URLSearchParams({
          tenantId,
          page: options.page || 1,
          limit: options.limit || 10,
          sortField: options.sortField || 'createdAt',
          sortOrder: options.sortOrder || 'DESC',
          search: options.search || '',
          includeExpired: options.includeExpired !== false ? 'true' : 'false'
        });
        
        const response = await apiClient.get(`/v1/application-users/invitations/pending?${params}`);
        return response;
      } catch (error) {
        console.error('Error fetching pending invitations:', error);
        throw error;
      }
    },
    resendInvitation: async (invitationId) => {
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }
      const response = await apiClient.post(`/v1/application-users/invite/${invitationId}/resend?tenantId=${tenantId}`);
      return response;
    },
    cancelInvitation: async (invitationId) => {
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }
      const response = await apiClient.delete(`/v1/application-users/invite/${invitationId}?tenantId=${tenantId}`);
      return response;
    },
    validateInvitation: async (token) => {
      const response = await apiClient.get(`/v1/application-users/invite/${token}/validate`);
      return response;
    },
    acceptInvitation: async (token, data) => {
      const response = await apiClient.post(`/v1/application-users/invite/${token}/accept`, data);
      return response;
    },
    deleteProfileImage: async (userId) => {
      try {
        if (!userId) throw new Error('User ID is required');
        const response = await apiClient.delete(`/v1/application-users/${userId}/profile-image`);
        return response.data;
      } catch (error) {
        console.error('Error deleting profile image:', error);
        throw error;
      }
    },
    updatePassword: async (passwordData) => {
      try {
        const response = await apiClient.put(
          '/v1/application-users/password',
          passwordData
        );
        return response.data;
      } catch (error) {
        console.error('Password update error:', error);
        console.error('Response:', error.response?.data);
        throw error;
      }
    },
    getUserActivityLogs: async (userId, options = {}) => {
      try {
        const response = await apiClient.get(`/v1/user-activity/user/${userId}`, {
          params: {
            page: options.page || 1,
            limit: options.limit || 50,
            category: options.category,
            startDate: options.startDate,
            endDate: options.endDate
          }
        });
        return response.data.activities;
      } catch (error) {
        console.error('Error fetching user activity logs:', error);
        throw error;
      }
    },
    updateCustomerUserStatus: async (userId, data) => {
      try {
        const response = await apiClient.put(`/v1/application-users/${userId}/status`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating customer user status:', error);
        throw error;
      }
    },
    convertToAppUser: async (userId, data) => {
      try {
        const tenantId = localStorage.getItem('tenantId');
        if (!tenantId) {
          throw new Error('Tenant ID is required');
        }
        const response = await apiClient.post(`/v1/application-users/convert/${userId}`, {
          ...data,
          tenantId
        });
        return response.data;
      } catch (error) {
        console.error('Error converting user to app user:', error);
        throw error;
      }
    },
    login: async (data) => {
      try {
        const response = await apiClient.post('/v1/auth/login', data);
        
        // Log login activity
        if (response.data?.user) {
          await apiClient.post('/v1/user-activity/login', {
            userId: response.data.user.id,
            customerId: response.data.user.customerId
          });
        }
        
        return response;
      } catch (error) {
        console.error('Error during login:', error);
        throw error;
      }
    },
    logout: async () => {
      try {
        const userId = localStorage.getItem('userId');
        const customerId = localStorage.getItem('customerId');
        
        // Log logout activity before actual logout
        if (userId && customerId) {
          await apiClient.post('/v1/user-activity/logout', {
            userId,
            customerId
          });
        }
        
        return apiClient.post('/v1/auth/logout');
      } catch (error) {
        console.error('Error during logout:', error);
        throw error;
      }
    },
    getApplicationUsers: (customerId, productId, tenantId, options = {}) =>
      apiClient.post('/v1/application-users/fetch/current/list', {
        customerId,
        productId,
        tenantID: tenantId,
        page: options.page || 1,
        limit: options.limit || 10,
        sortField: options.sortField || 'fullName',
        sortOrder: options.sortOrder || 'asc',
        search: options.search || ''
      }),
    getOrganizationUsers: (customerId, options = {}) =>
      apiClient.post('/v1/application-users/fetch/orgusers/list', {
        customerId,
        productId: options.productId,
        tenantID: options.tenantID,
        page: options.page || 1,
        limit: options.limit || 10,
        sortField: options.sortField || 'fullName',
        sortOrder: options.sortOrder || 'asc',
        search: options.search || ''
      }),
  }), []);

  const licenses = useMemo(() => ({
    getStatus: () => apiClient.get('/v1/licenses/status'),
    validate: () => apiClient.get('/v1/licenses/validate'),
    getProductFeatures: async (licenseId) => {
      const response = await apiClient.get(`/v1/licenses/${licenseId}/product-features`);
      return response.data;
    },
    getFeatureHistory: async (licenseId) => {
      const response = await apiClient.get(`/v1/licenses/${licenseId}/features/history`);
      return response.data;
    },
    updateFeatures: async (licenseId, metadata) => {
      const response = await apiClient.put(`/v1/licenses/${licenseId}/features`, {
        features: metadata.features,
        featureHistory: metadata.featureHistory
      });
      return response.data;
    }
  }), []);

  const billing = useMemo(() => ({
    getBills: () => apiClient.get('/v1/bills'),
    getBill: (billId) => apiClient.get(`/v1/bills/${billId}`),
    payBill: (billId) => apiClient.post(`/v1/bills/${billId}/pay`),
    downloadInvoice: (billId) => apiClient.get(`/v1/bills/${billId}/invoice/download`),
    viewBill: (bill) => {
      const paymentUrl = appConfig.paymentUrl;
      window.open(`${paymentUrl}?token=${bill.viewToken}`, '_blank');
    }
  }), []);

  const onboarding = useMemo(() => ({
    start: async (data) => {
      const productId = data.productId ?? appConfig.productId;
      const response = await apiClient.post('/v1/onboarding/start', {
        ...data,
        productId
      }, { skipAuthRedirect: true });
      return response.data;
    },
    getProductInfo: async (productIdParam) => {
      const productId = productIdParam ?? appConfig.productId;
      if (productId == null || productId === '') {
        throw new Error('Product ID is not configured');
      }
      const response = await apiClient.get(`/v1/onboarding/product-info/${productId}`, { skipAuthRedirect: true });
      return response.data;
    },
    verifyEmail: async (token) => {
      const response = await apiClient.post('/v1/onboarding/verify-email', { token }, { skipAuthRedirect: true });
      return response.data;
    },
    resendVerification: (email) => apiClient.post('/v1/onboarding/resend-verification', { email }, { skipAuthRedirect: true }),
  }), []);

  const customer = useMemo(() => ({
    getCustomerInfo: () => apiClient.get('/v1/customer/info'),
    updateCustomerInfo: (data) => apiClient.put('/v1/customer/info', data),
    getCustomerProfile: () => apiClient.get('/v1/customer/profile')
  }), []);

  return {
    api: apiClient,
    auth,
    users,
    licenses,
    billing,
    onboarding,
    customer,
  };
}
